import { useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { flowsheetUrl } from './api';
const headers = 'mrn,first_name,last_name,date_of_birth,edd,fetus_count,gravida,term,preterm,abortions,living';
interface Preview {
  preview_hash: string;
  can_import: boolean;
  rows: {
    line: number;
    mrn: string;
    first_name: string;
    last_name: string;
    date_of_birth: string;
    edd: string;
    fetus_count: number;
    gravida: number;
    term: number;
    preterm: number;
    abortions: number;
    living: number;
    action: string;
    reason?: string;
  }[];
  errors: { line: number; message: string }[];
}
export function PracticeOnboarding() {
  const { organization } = useAuth();
  return organization ? (
    <OnboardingForm key={organization.organization_id} orgId={organization.organization_id} />
  ) : null;
}
function OnboardingForm({ orgId }: { orgId: string }) {
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<Preview>();
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [retry, setRetry] = useState(false);
  const attempt = useRef<{ csv_text: string; expected_hash: string; request_id: string; reviewed: boolean }>();
  const generation = useRef(0);
  async function load(file?: File) {
    const current = ++generation.current;
    setPreview(undefined);
    setReviewed(false);
    setCsv('');
    setMessage('');
    if (!file) return;
    if (file.size > 300000) {
      setMessage('Choose a CSV smaller than 300 KB.');
      return;
    }
    try {
      const text = await file.text();
      if (current === generation.current) setCsv(text);
    } catch {
      if (current === generation.current) setMessage('Could not read this file.');
    }
  }
  async function runPreview() {
    const current = ++generation.current;
    setBusy(true);
    setMessage('');
    setPreview(undefined);
    setReviewed(false);
    try {
      const { data } = await clientApi.post<Preview>(`${flowsheetUrl(orgId)}/onboarding/preview`, { csv_text: csv });
      if (current === generation.current) setPreview(data);
    } catch (e) {
      if (current === generation.current) setMessage(errorMessage(e));
    } finally {
      if (current === generation.current) setBusy(false);
    }
  }
  async function commit() {
    if (!preview || !reviewed) return;
    const body = attempt.current ?? {
      csv_text: csv,
      expected_hash: preview.preview_hash,
      request_id: crypto.randomUUID(),
      reviewed: true,
    };
    attempt.current = body;
    setBusy(true);
    setMessage('');
    try {
      await clientApi.post(`${flowsheetUrl(orgId)}/onboarding/import`, body);
      setMessage(
        'Import complete. Open Patients to review the imported pregnancies. Clinical flowsheets are started separately by an authorized clinician.',
      );
      setPreview(undefined);
      setCsv('');
      setReviewed(false);
      setRetry(false);
      attempt.current = undefined;
    } catch (e) {
      const definitive = isAxiosError(e) && e.response && e.response.status >= 400 && e.response.status < 500;
      setRetry(!definitive);
      if (definitive) {
        attempt.current = undefined;
        setPreview(undefined);
        setReviewed(false);
      }
      setMessage(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <details>
      <summary>Import active clients from CSV</summary>
      <div className="flex flex-col gap-4 py-4">
        <p>
          Review identities, EDDs, fetus counts and obstetric history before importing. Existing charts are retained;
          conflicting identities or pregnancies require manual reconciliation. Up to 500 rows per import.
        </p>
        <Button
          variant="outline"
          onClick={() => {
            const url = URL.createObjectURL(new Blob([headers + '\n'], { type: 'text/csv' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'active-clients-template.csv';
            a.click();
            URL.revokeObjectURL(url);
          }}>
          Download blank CSV template
        </Button>
        <p>
          Use YYYY-MM-DD dates and explicit whole-number counts. Do not substitute zero for unknown obstetric history.
          EDD dating methods are left unspecified for clinical review.
        </p>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="clients-csv">Active-client CSV</FieldLabel>
            <Input
              id="clients-csv"
              type="file"
              accept=".csv,text/csv"
              disabled={busy || retry}
              onChange={(e) => void load(e.target.files?.[0])}
            />
          </Field>
        </FieldGroup>
        <Button variant="outline" disabled={!csv || busy || retry} onClick={() => void runPreview()}>
          Preview import
        </Button>
        {preview && (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {['Row', 'MRN', 'Client', 'DOB', 'EDD', 'Fetuses', 'G/T/P/A/L', 'Action'].map((x) => (
                      <TableHead key={x}>{x}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r) => (
                    <TableRow key={r.line}>
                      <TableCell>{r.line}</TableCell>
                      <TableCell>{r.mrn}</TableCell>
                      <TableCell>
                        {r.first_name} {r.last_name}
                      </TableCell>
                      <TableCell>{r.date_of_birth}</TableCell>
                      <TableCell>{r.edd}</TableCell>
                      <TableCell>{r.fetus_count}</TableCell>
                      <TableCell>{[r.gravida, r.term, r.preterm, r.abortions, r.living].join('/')}</TableCell>
                      <TableCell>
                        {r.action.replaceAll('_', ' ')} {r.reason}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {preview.errors.map((e) => (
              <p role="alert" key={e.line}>
                Row {e.line}: {e.message}
              </p>
            ))}
            <Field orientation="horizontal">
              <Checkbox
                id="clients-reviewed"
                checked={reviewed}
                disabled={busy || retry || !preview.can_import}
                onCheckedChange={(v) => setReviewed(v === true)}
              />
              <FieldLabel htmlFor="clients-reviewed">
                I verified every identity, EDD, fetus count and obstetric count against the source.
              </FieldLabel>
            </Field>
            <Button disabled={busy || !preview.can_import || !reviewed} onClick={() => void commit()}>
              {retry ? 'Retry the same import' : 'Import reviewed clients'}
            </Button>
          </>
        )}
        {retry && (
          <p>Confirmation was not received. Retry the same import to recover its result without duplicating clients.</p>
        )}
        {message && (
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}
      </div>
    </details>
  );
}
function errorMessage(error: unknown) {
  return isAxiosError(error) && typeof error.response?.data?.detail === 'string'
    ? error.response.data.detail
    : 'The request could not be completed. Please retry.';
}
